import type { Transition, Variants } from 'framer-motion';

/**
 * Shared motion vocabulary for the Student Journey.
 * Durations/eases mirror the `--sp-*` CSS tokens in scss/studentJourneyTheme.scss
 * and the Landing page's framer-motion cadence. Every consumer should still gate
 * with `useReducedMotion()` — `AnimatePresence`/variants below are the "full" set.
 */

export const spDuration = {
  fast: 0.12,
  base: 0.2,
  slow: 0.38,
} as const;

type CubicBezier = [number, number, number, number];

export const spEase = {
  standard: [0.4, 0, 0.2, 1] as CubicBezier,
  out: [0.16, 1, 0.3, 1] as CubicBezier,
  spring: [0.34, 1.56, 0.64, 1] as CubicBezier,
};

export const spSpring: Transition = { type: 'spring', stiffness: 420, damping: 32, mass: 0.9 };
export const spSpringSoft: Transition = { type: 'spring', stiffness: 260, damping: 26 };

/** Section / card entrance — fade + rise. Stagger children with `spStagger`. */
export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: spDuration.base, ease: spEase.out } },
};

export const spStagger = (stagger = 0.06, delayChildren = 0): Variants => ({
  hidden: {},
  show: { transition: { staggerChildren: stagger, delayChildren } },
});

/** Modal / popover panel. */
export const popPanel: Variants = {
  hidden: { opacity: 0, scale: 0.96, y: 8 },
  show: { opacity: 1, scale: 1, y: 0, transition: spSpringSoft },
  exit: { opacity: 0, scale: 0.98, y: 4, transition: { duration: spDuration.fast } },
};

export const fade: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: spDuration.base } },
  exit: { opacity: 0, transition: { duration: spDuration.fast } },
};

/** Celebration pop — for milestone / success moments. */
export const celebratePop: Variants = {
  hidden: { opacity: 0, scale: 0.7 },
  show: { opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 500, damping: 18 } },
};

/** Convenience: collapse a variant set to a no-op when the user prefers reduced motion. */
export const maybe = (v: Variants, reduced: boolean | null): Variants =>
  reduced ? { hidden: { opacity: 1 }, show: { opacity: 1 }, exit: { opacity: 1 } } : v;
